/**
 * MapSearch: place search floating on the map (top-left, the MapLibre
 * convention, stacked where nav controls would go).
 *
 * Suggestions appear as you type (GET /v1/places/suggest: the
 * jurisdiction's own place names first, then OpenStreetMap type-ahead), with
 * the typed text highlighted. The last row, "Search for …", runs the full
 * search (GET /v1/places/search) for addresses the suggestions miss. When the
 * top suggestion starts with the typed text, the rest of its name shows in
 * the box in grey (Tab or → accepts it). If street suggestions can't be
 * reached, the list says so instead of looking empty. Pick a
 * place (click, or arrow keys + Enter) to fly there and drop a marker; areas
 * such as neighborhoods are fitted to the view. No auth needed -- guests use
 * it too.
 *
 * Accessibility: combobox/listbox roles, arrow-key navigation, Esc closes,
 * a visible label for screen readers, and reduced-motion users get an
 * instant jump instead of the flyTo animation.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import maplibregl from 'maplibre-gl';
import { ApiError } from '../../lib/api';
import { searchPlaces, suggestPlaces, type PlaceResult } from '../../lib/places';
import { SearchIcon } from '../icons';

const DEBOUNCE_MS = 160;
const MIN_CHARS = 2;
const FOCUS_ZOOM = 13;

type Mode = 'suggest' | 'results';

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** The place name with the typed text in bold (first match, any case). */
function Highlighted({ text, query }: { text: string; query: string }): JSX.Element {
  const q = query.trim();
  const at = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <strong className="font-bold text-ink">{text.slice(at, at + q.length)}</strong>
      {text.slice(at + q.length)}
    </>
  );
}

export function MapSearch({ map, className }: { map: maplibregl.Map; className?: string }): JSX.Element {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [places, setPlaces] = useState<PlaceResult[]>([]);
  const [mode, setMode] = useState<Mode>('suggest');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streets, setStreets] = useState<'ok' | 'disabled' | 'unavailable'>('ok');

  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const requestRef = useRef(0);
  /** Set when we change the text ourselves (picking a place) to skip a refetch. */
  const skipFetchRef = useRef(false);

  const trimmed = query.trim();
  const ready = trimmed.length >= MIN_CHARS;
  // In suggest mode the last row is "Search for …".
  const rowCount = mode === 'suggest' && ready ? places.length + 1 : places.length;

  // Suggestions while typing (debounced; stale responses are ignored).
  useEffect(() => {
    if (skipFetchRef.current) {
      skipFetchRef.current = false;
      return;
    }
    setMode('suggest');
    setError(null);
    if (!ready) {
      setPlaces([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    const id = ++requestRef.current;
    const timer = window.setTimeout(() => {
      void suggestPlaces(trimmed, i18n.language)
        .then((res) => {
          if (id !== requestRef.current) return;
          setPlaces(res.places);
          setStreets(res.streets);
          setActiveIndex(0);
          setOpen(true);
        })
        .catch(() => {
          // Suggestions are best effort; "Search for …" still works.
          if (id !== requestRef.current) return;
          setPlaces([]);
          setStreets('unavailable');
          setActiveIndex(0);
          setOpen(true);
        });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Close the dropdown on outside click.
  useEffect(() => {
    function onPointerDown(event: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  // Tear down the marker on unmount.
  useEffect(
    () => () => {
      markerRef.current?.remove();
      markerRef.current = null;
    },
    [],
  );

  function clearMarker(): void {
    markerRef.current?.remove();
    markerRef.current = null;
  }

  function goTo(place: PlaceResult): void {
    clearMarker();
    markerRef.current = new maplibregl.Marker({ color: '#1C2A33' }).setLngLat([place.lon, place.lat]).addTo(map);
    const animate = !prefersReducedMotion();
    if (place.extent) {
      const [w, s, e, n] = place.extent;
      map.fitBounds(
        [
          [w, s],
          [e, n],
        ],
        { padding: 60, maxZoom: 15, duration: animate ? 1200 : 0 },
      );
    } else {
      const zoom = Math.max(map.getZoom(), FOCUS_ZOOM);
      if (animate) map.flyTo({ center: [place.lon, place.lat], zoom, duration: 1200 });
      else map.jumpTo({ center: [place.lon, place.lat], zoom });
    }
    // Cancel any lookup still in flight (e.g. from accepting a completion
    // just before Enter) so its answer can't reopen the list.
    requestRef.current += 1;
    if (place.name !== query) {
      skipFetchRef.current = true;
      setQuery(place.name);
    }
    setOpen(false);
    inputRef.current?.blur();
  }

  function runFullSearch(): void {
    if (!ready) return;
    const id = ++requestRef.current;
    setMode('results');
    setLoading(true);
    setError(null);
    setPlaces([]);
    setOpen(true);
    void searchPlaces(trimmed)
      .then((hits) => {
        if (id !== requestRef.current) return;
        setPlaces(hits);
        setActiveIndex(0);
      })
      .catch((err: unknown) => {
        if (id !== requestRef.current) return;
        setError(err instanceof ApiError && err.status === 400 ? t('search.tooShort') : t('search.unavailable'));
      })
      .finally(() => {
        if (id === requestRef.current) setLoading(false);
      });
  }

  function activate(index: number): void {
    const place = places[index];
    if (place) goTo(place);
    else if (mode === 'suggest' && index === places.length) runFullSearch();
  }

  function handleClear(): void {
    requestRef.current += 1;
    setQuery('');
    setPlaces([]);
    setOpen(false);
    setError(null);
    setLoading(false);
    clearMarker();
    inputRef.current?.focus();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    const atEnd =
      event.currentTarget.selectionStart === query.length && event.currentTarget.selectionEnd === query.length;
    if ((event.key === 'Tab' && !event.shiftKey) || (event.key === 'ArrowRight' && atEnd)) {
      if (acceptCompletion()) event.preventDefault();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        if (rowCount > 0) setOpen(true);
        return;
      }
      setActiveIndex((i) => (i + 1) % Math.max(rowCount, 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (open && rowCount > 0) setActiveIndex((i) => (i - 1 + rowCount) % rowCount);
    } else if (event.key === 'Enter') {
      if (!ready) return;
      event.preventDefault();
      if (open && rowCount > 0) activate(Math.min(activeIndex, rowCount - 1));
      else runFullSearch();
    } else if (event.key === 'Escape') {
      if (open) {
        event.preventDefault();
        setOpen(false);
      }
    }
  }

  // Inline completion: the rest of the top suggestion's name, when it starts
  // with what was typed (shown in grey; Tab or → at the end accepts it).
  const top = places[0];
  const completion =
    mode === 'suggest' &&
    open &&
    ready &&
    activeIndex === 0 &&
    top &&
    top.name.length > query.length &&
    top.name.toLowerCase().startsWith(query.toLowerCase())
      ? top.name.slice(query.length)
      : '';

  function acceptCompletion(): boolean {
    if (!completion || !top) return false;
    setQuery(top.name);
    return true;
  }

  const listId = 'map-search-results';
  const optionId = (i: number): string => `map-search-option-${i}`;
  const showDropdown = open && ready;

  return (
    <div ref={rootRef} className={className ?? 'absolute left-3 top-3 z-20 w-60 max-w-[calc(100%-1.5rem)] sm:w-72'}>
      <label htmlFor="map-place-search" className="sr-only">
        {t('search.label')}
      </label>
      <div className="relative">
        <span aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-stone-400">
          <SearchIcon size={16} />
        </span>
        <input
          ref={inputRef}
          id="map-place-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (ready && (places.length > 0 || mode === 'suggest')) setOpen(true);
          }}
          placeholder={t('search.placeholder')}
          autoComplete="off"
          spellCheck={false}
          role="combobox"
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls={listId}
          aria-activedescendant={showDropdown && rowCount > 0 ? optionId(activeIndex) : undefined}
          className="h-10 w-full rounded-xl border border-stone-200/80 bg-white/95 pl-9 pr-8 text-sm shadow-float backdrop-blur placeholder:text-stone-400 focus:border-tenant-primary focus:outline-none focus:ring-2 focus:ring-tenant-tint"
        />
        {completion && (
          // Same box metrics as the input: the typed part is invisible, the
          // rest of the suggestion shows in grey right after the caret.
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 right-8 flex items-center overflow-hidden whitespace-pre border border-transparent pl-9 text-sm"
          >
            <span className="invisible">{query}</span>
            <span className="text-stone-400">{completion}</span>
          </span>
        )}
        {query && (
          <button
            type="button"
            onClick={handleClear}
            aria-label={t('search.clear')}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-1 text-stone-400 hover:text-stone-700"
          >
            ✕
          </button>
        )}
      </div>
      {showDropdown && (
        <ul
          id={listId}
          role="listbox"
          aria-label={t('search.resultsLabel')}
          className="mt-1 max-h-72 overflow-y-auto rounded-xl border border-stone-200 bg-white py-1 shadow-float"
        >
          {loading && (
            <li className="px-3 py-2 text-sm text-stone-500" aria-live="polite">
              {t('search.searching')}
            </li>
          )}
          {!loading && error && (
            <li className="px-3 py-2 text-sm text-red-700" role="alert">
              {error}
            </li>
          )}
          {!loading && !error && mode === 'results' && places.length === 0 && (
            <li className="px-3 py-2 text-sm text-stone-500">{t('search.noResults')}</li>
          )}
          {!loading &&
            places.map((place, i) => (
              <li
                key={`${place.lat},${place.lon},${i}`}
                id={optionId(i)}
                role="option"
                aria-selected={i === activeIndex}
                onMouseDown={(e) => {
                  // Select before the outside-click handler closes us.
                  e.preventDefault();
                  goTo(place);
                }}
                onMouseEnter={() => setActiveIndex(i)}
                className={`cursor-pointer px-3 py-2 ${i === activeIndex ? 'bg-tenant-soft' : 'bg-white'}`}
              >
                <p className="flex items-center gap-1.5 text-sm text-stone-700">
                  <span className="min-w-0 truncate">
                    <Highlighted text={place.name} query={trimmed} />
                  </span>
                  {place.source === 'local' && (
                    <span className="shrink-0 rounded bg-stone-100 px-1 py-px text-[10px] font-semibold uppercase tracking-wide text-stone-500">
                      {t('search.local')}
                    </span>
                  )}
                </p>
                {place.source !== 'local' && place.label && (
                  <p className="truncate text-xs text-stone-500">{place.label}</p>
                )}
              </li>
            ))}
          {mode === 'suggest' && streets === 'unavailable' && (
            <li role="presentation" className="px-3 pb-1 pt-2 text-[11px] leading-snug text-stone-500">
              {t('search.streetsUnavailable')}
            </li>
          )}
          {mode === 'suggest' && (
            <li
              id={optionId(places.length)}
              role="option"
              aria-selected={activeIndex === places.length}
              onMouseDown={(e) => {
                e.preventDefault();
                runFullSearch();
              }}
              onMouseEnter={() => setActiveIndex(places.length)}
              className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-stone-600 ${
                places.length > 0 ? 'border-t border-stone-100' : ''
              } ${activeIndex === places.length ? 'bg-tenant-soft' : 'bg-white'}`}
            >
              <SearchIcon size={14} />
              <span className="min-w-0 truncate">{t('search.searchFor', { query: trimmed })}</span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
